import { axiosInstance } from "@/lib/axiosInstance";
import config from "@/lib/config";
import axios from "axios";
import { PaginatedResponse } from "@/shared/models/pagination.model";

export interface CompanyManagement {
  _id: string;
  name: string;
  companyAdmin?: {
    _id: string;
    name?: string;
    email: string;
  };
  address?: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    _id: string;
    name: string;
  };
  updatedBy?: {
    _id: string;
    name: string;
  };
}

export interface FetchCompaniesParams {
  page?: string | number;
  limit?: string | number;
  startCreatedDate?: string;
  endCreatedDate?: string;
  startUpdatedDate?: string;
  endUpdatedDate?: string;
  name?: string;
  companyAdmin?: string;
  sortBy?: string;
  sortOrder?: string;
}

export async function fetchCompanies(params: FetchCompaniesParams = {}): Promise<PaginatedResponse> {
  try {
    const queryParams = new URLSearchParams();

    // Add all non-empty parameters to the query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.set(key, value.toString());
      }
    });

    // Set defaults if not provided
    if (!queryParams.has("page")) {
      queryParams.set("page", "1");
    }
    if (!queryParams.has("limit")) {
      queryParams.set("limit", "10");
    }
    if (!queryParams.has("sortBy")) {
      queryParams.set("sortBy", "createdAt");
    }
    if (!queryParams.has("sortOrder")) {
      queryParams.set("sortOrder", "desc");
    }

    const response = await axiosInstance.get(
      `${config.apiUrl}/companies?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.message || "Error fetching companies");
    } else {
      throw new Error("An unknown error occurred");
    }
  }
}

export async function getCompany(id: string): Promise<CompanyManagement> {
  try {
    const response = await axiosInstance.get(`${config.apiUrl}/companies/${id}`);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.message || "Error fetching company");
    } else {
      throw new Error("An unknown error occurred");
    }
  }
}

export async function createCompany(data: {
  name: string;
  companyAdmin?: string;
  address?: string;
  phone?: string;
}): Promise<CompanyManagement> {
  try {
    const response = await axiosInstance.post(`${config.apiUrl}/companies`, data);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.message || "Error creating company");
    } else {
      throw new Error("An unknown error occurred");
    }
  }
}

export async function updateCompany(id: string, data: {
  name?: string;
  companyAdmin?: string;
  address?: string;
  phone?: string;
}): Promise<CompanyManagement> {
  try {
    const response = await axiosInstance.patch(`${config.apiUrl}/companies/${id}`, data);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.message || "Error updating company");
    } else {
      throw new Error("An unknown error occurred");
    }
  }
}

export async function deleteCompany(id: string, hardDelete: boolean = false): Promise<void> {
  try {
    const params = hardDelete ? { hardDelete: "true" } : {};
    await axiosInstance.delete(`${config.apiUrl}/companies/${id}`, { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.message || "Error deleting company");
    } else {
      throw new Error("An unknown error occurred");
    }
  }
}
